import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import Input from '../Inputs/input/input';
import { Button } from '../index';
import type { ButtonProps } from '../button/button';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import './editable-text.scss';

interface Props {
	value: string;
	disabled?: boolean;
	onValueChange(newValue: string): void;
	onEnter?(newValue: string): void;
	className?: string;
	buttonType?: ButtonProps['type'];
	placeholder?: string;
	maxLength?: number;
	editAllowed?: boolean;
	showEditButtonOnHover?: boolean;
}

const ESCAPE = 27;
const EditableText = ({
	disabled = false,
	value,
	onValueChange,
	onEnter,
	className,
	buttonType,
	placeholder,
	maxLength,
	editAllowed = true,
	showEditButtonOnHover = false
}: Props) => {
	const [currentValue, setCurrentValue] = useState<string>(value);
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [beforeChangeValue, setBeforeChangeValue] = useState<string>(currentValue);

	useEffect(() => {
		setCurrentValue(value);
	}, [value]);

	const onKeyDown = e => {
		if (e.keyCode === ESCAPE) {
			setCurrentValue(beforeChangeValue);
			setIsEditMode(false);
		}
	};
	const onChange = e => {
		setCurrentValue(e.target.value);
		onValueChange?.(e.target.value);
	};
	const onInputClick = e => {
		e.stopPropagation();
	};
	const applyChanges = e => {
		e.preventDefault();

		setBeforeChangeValue(currentValue);
		setIsEditMode(false);

		if (onEnter) {
			onEnter(currentValue);
		}
	};
	const toggleEdit = e => {
		e.stopPropagation();

		if (isEditMode && onEnter) {
			onEnter(currentValue);
		}

		// Changing to be in edit mode now
		if (!isEditMode) {
			// save the value so can discard changes
			setBeforeChangeValue(currentValue);
		}
		setIsEditMode(prev => !prev);
	};

	const icon = isEditMode ? BringgFontIcons.Selected : BringgFontIcons.Pencil;

	return (
		<div data-test-id="editable-text" className={classNames('editable-block', className)}>
			{isEditMode && !disabled ? (
				<Input
					type="text"
					className="editable-input"
					value={currentValue}
					onChange={onChange}
					onClick={onInputClick}
					onPressEnter={applyChanges}
					onKeyDown={onKeyDown}
					autoFocus
					placeholder={placeholder}
					maxLength={maxLength}
				/>
			) : (
				<div className={classNames('title', { disabled })} title={currentValue || placeholder}>
					{currentValue || placeholder}
				</div>
			)}
			{editAllowed && (
				<Button
					shape="circle"
					disabled={disabled}
					icon={<BringgIcon iconName={icon} />}
					className={classNames('edit-icon-button', {
						'button-by-hover': showEditButtonOnHover,
						'edit-mode': isEditMode && showEditButtonOnHover
					})}
					onClick={toggleEdit}
					type={buttonType}
				/>
			)}
		</div>
	);
};

export default EditableText;
